/**
 * Consent Callback Page
 *
 * Handles the redirect from Azure AD after admin consent flow.
 * Displays success or error state based on URL parameters.
 */

import { useSearchParams, Link } from 'react-router-dom';
import {
    Card,
    CardHeader,
    Text,
    Title2,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import {
    CheckmarkCircle24Filled,
    ErrorCircle24Filled,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: tokens.colorNeutralBackground2,
        padding: tokens.spacingHorizontalXXL,
    },
    card: {
        maxWidth: '480px',
        width: '100%',
        padding: tokens.spacingHorizontalXXL,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        marginBottom: tokens.spacingVerticalL,
    },
    successIcon: {
        color: tokens.colorPaletteGreenForeground1,
        fontSize: '32px',
    },
    errorIcon: {
        color: tokens.colorPaletteRedForeground1,
        fontSize: '32px',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    link: {
        marginTop: tokens.spacingVerticalL,
        color: tokens.colorBrandForeground1,
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    errorCode: {
        fontFamily: tokens.fontFamilyMonospace,
        backgroundColor: tokens.colorNeutralBackground3,
        padding: tokens.spacingHorizontalS,
        borderRadius: tokens.borderRadiusMedium,
    },
});

export function ConsentCallbackPage(): JSX.Element {
    const styles = useStyles();
    const [searchParams] = useSearchParams();

    const consent = searchParams.get('consent');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    const isSuccess = consent === 'success';
    const isError = consent === 'error' || error;

    return (
        <div className={styles.container}>
            <Card className={styles.card}>
                {isSuccess && (
                    <>
                        <CardHeader
                            header={
                                <div className={styles.header}>
                                    <CheckmarkCircle24Filled className={styles.successIcon} />
                                    <Title2>Consent Granted</Title2>
                                </div>
                            }
                        />
                        <div className={styles.content}>
                            <Text>
                                Admin consent has been successfully granted for your organization.
                                You can now sign in with your organizational account.
                            </Text>
                            <Link to="/login" className={styles.link}>
                                Continue to Login
                            </Link>
                        </div>
                    </>
                )}

                {isError && (
                    <>
                        <CardHeader
                            header={
                                <div className={styles.header}>
                                    <ErrorCircle24Filled className={styles.errorIcon} />
                                    <Title2>Consent Failed</Title2>
                                </div>
                            }
                        />
                        <div className={styles.content}>
                            <Text>
                                There was a problem granting admin consent for your organization.
                            </Text>
                            {error && (
                                <Text>
                                    Error code: <span className={styles.errorCode}>{error}</span>
                                </Text>
                            )}
                            {errorDescription && (
                                <Text>{decodeURIComponent(errorDescription)}</Text>
                            )}
                            <Link to="/login" className={styles.link}>
                                Try Again
                            </Link>
                        </div>
                    </>
                )}

                {!isSuccess && !isError && (
                    <>
                        <CardHeader
                            header={
                                <div className={styles.header}>
                                    <Title2>Invalid Request</Title2>
                                </div>
                            }
                        />
                        <div className={styles.content}>
                            <Text>
                                This page was accessed without valid consent parameters.
                            </Text>
                            <Link to="/login" className={styles.link}>
                                Go to Login
                            </Link>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
}
